import type { ComponentProps } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { tv } from "tailwind-variants";

import {
  getRelevantSideEffects,
  RELIEF_OPTIONS,
  SIDE_EFFECT_INTENSITY_OPTIONS,
} from "@/domains/profile/models/constants";
import type { HpFeedbackForm, VerifiedProduct } from "@/domains/profile/models/types";
import { Button, Checkbox, Heading, RadioGroup } from "@/ui";

export type FeedbackQuestion = "dosage" | "relief" | "sideEffects" | "sideEffectsIntensity";

const productFeedbackStepVariants = tv({
  slots: {
    container: "flex flex-col gap-6",
    productCard: "flex items-center gap-3 rounded-lg border border-line bg-surface p-4",
    productImage: "size-20 rounded-xl object-cover",
    fieldset: "flex flex-col gap-2",
    legend: "mb-2 font-semibold",
    radioLabel: "flex cursor-pointer items-center gap-2 rounded-lg border p-3",
  },
  variants: {
    selected: {
      true: { radioLabel: "border-accent-line bg-accent-surface" },
      false: { radioLabel: "border-line bg-surface" },
    },
  },
});

const { container, fieldset, legend, productCard, productImage, radioLabel } =
  productFeedbackStepVariants();

const QUESTION_TITLE: Record<FeedbackQuestion, string> = {
  dosage: "When you use it, how much do you typically take?",
  relief: "How much relief are you getting?",
  sideEffects: "Any unwanted side effects?",
  sideEffectsIntensity: "How intense was each one?",
};

type ProductFeedbackStepProps = ComponentProps<"div"> & {
  onContinue: () => void;
  product: VerifiedProduct;
  question: FeedbackQuestion;
};

export const ProductFeedbackStep = ({
  className,
  onContinue,
  product,
  question,
}: ProductFeedbackStepProps) => {
  const { control, setValue, watch } = useFormContext<HpFeedbackForm>();
  const feedback = watch(`feedback.${product.sku}`);
  const relevantSideEffects = getRelevantSideEffects(feedback?.sideEffects);

  const storedDose = product.doses.find((dose) => {
    return dose.id === feedback?.dosageId;
  });

  const selectedDoseId =
    storedDose?.id ??
    product.doses.find((dose) => {
      return dose.key === feedback?.dosage;
    })?.id ??
    "";

  const isAnswered = (() => {
    switch (question) {
      case "dosage":
        return Boolean(feedback?.dosage);
      case "relief":
        return Boolean(feedback?.relief);
      case "sideEffects":
        return (
          feedback?.hasSideEffects !== undefined &&
          (!feedback.hasSideEffects || (feedback.sideEffects?.length ?? 0) > 0)
        );
      case "sideEffectsIntensity":
        return relevantSideEffects.every((sideEffect) => {
          return Boolean(feedback?.sideEffectIntensities?.[sideEffect]);
        });
    }
  })();

  return (
    <div className={container({ className })}>
      <div className={productCard()}>
        <img alt={product.name} className={productImage()} src={product.url} />
        <span className="font-medium">{product.name}</span>
      </div>

      <Heading className="text-2xl sm:text-2xl" level={2}>
        {QUESTION_TITLE[question]}
      </Heading>

      {question === "dosage" && (
        <Controller
          control={control}
          name={`feedback.${product.sku}.dosage`}
          render={({ field }) => {
            const handleDoseChange = (doseId: string) => {
              const picked = product.doses.find((dose) => {
                return dose.id === doseId;
              });

              setValue(`feedback.${product.sku}.dosageId`, doseId);
              field.onChange(picked?.key ?? "");
            };

            return (
              <fieldset className={fieldset()}>
                <RadioGroup.Root onValueChange={handleDoseChange} value={selectedDoseId}>
                  {product.doses.map((dose) => {
                    return (
                      <label
                        className={radioLabel({ selected: selectedDoseId === dose.id })}
                        key={dose.id}
                      >
                        <RadioGroup.Item value={dose.id}>
                          <RadioGroup.Indicator />
                        </RadioGroup.Item>
                        <span>{dose.value}</span>
                      </label>
                    );
                  })}
                </RadioGroup.Root>
              </fieldset>
            );
          }}
        />
      )}

      {question === "relief" && (
        <Controller
          control={control}
          name={`feedback.${product.sku}.relief`}
          render={({ field }) => {
            return (
              <fieldset className={fieldset()}>
                <RadioGroup.Root onValueChange={field.onChange} value={field.value || ""}>
                  {RELIEF_OPTIONS.map((option) => {
                    return (
                      <label
                        className={radioLabel({ selected: field.value === option.value })}
                        key={option.value}
                      >
                        <RadioGroup.Item value={option.value}>
                          <RadioGroup.Indicator />
                        </RadioGroup.Item>
                        <span>{option.label}</span>
                      </label>
                    );
                  })}
                </RadioGroup.Root>
              </fieldset>
            );
          }}
        />
      )}

      {question === "sideEffects" && (
        <>
          <Controller
            control={control}
            name={`feedback.${product.sku}.hasSideEffects`}
            render={({ field }) => {
              return (
                <fieldset className={fieldset()}>
                  <RadioGroup.Root
                    onValueChange={(value) => {
                      return field.onChange(value === "true");
                    }}
                    value={field.value === undefined ? "" : String(field.value)}
                  >
                    <label className={radioLabel({ selected: field.value === true })}>
                      <RadioGroup.Item value="true">
                        <RadioGroup.Indicator />
                      </RadioGroup.Item>
                      <span>Yes</span>
                    </label>
                    <label className={radioLabel({ selected: field.value === false })}>
                      <RadioGroup.Item value="false">
                        <RadioGroup.Indicator />
                      </RadioGroup.Item>
                      <span>No</span>
                    </label>
                  </RadioGroup.Root>
                </fieldset>
              );
            }}
          />

          {feedback?.hasSideEffects === true && (
            <Controller
              control={control}
              name={`feedback.${product.sku}.sideEffects`}
              render={({ field }) => {
                const selected: string[] = field.value || [];

                const handleToggle = (option: string, checked: boolean) => {
                  const updated = checked
                    ? [...selected, option]
                    : selected.filter((item) => {
                        return item !== option;
                      });
                  field.onChange(updated);
                };

                return (
                  <fieldset className={fieldset()}>
                    <legend className={legend()}>Which side effects have you experienced?</legend>
                    {product.sideEffects.map((option) => {
                      const isChecked = selected.includes(option);

                      return (
                        <label className={radioLabel({ selected: isChecked })} key={option}>
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              return handleToggle(option, Boolean(checked));
                            }}
                          />
                          <span>{option}</span>
                        </label>
                      );
                    })}
                  </fieldset>
                );
              }}
            />
          )}
        </>
      )}

      {question === "sideEffectsIntensity" && (
        <Controller
          control={control}
          name={`feedback.${product.sku}.sideEffectIntensities`}
          render={({ field }) => {
            const value: Record<string, number> = field.value || {};

            return (
              <>
                {relevantSideEffects.map((sideEffect) => {
                  return (
                    <fieldset className={fieldset()} key={sideEffect}>
                      <legend className={legend()}>{sideEffect}</legend>
                      <RadioGroup.Root
                        onValueChange={(next) => {
                          return field.onChange({ ...value, [sideEffect]: Number(next) });
                        }}
                        value={value[sideEffect] ? String(value[sideEffect]) : ""}
                      >
                        {SIDE_EFFECT_INTENSITY_OPTIONS.map((option) => {
                          return (
                            <label
                              className={radioLabel({
                                selected: value[sideEffect] === option.value,
                              })}
                              key={option.value}
                            >
                              <RadioGroup.Item value={String(option.value)}>
                                <RadioGroup.Indicator />
                              </RadioGroup.Item>
                              <span>{option.label}</span>
                            </label>
                          );
                        })}
                      </RadioGroup.Root>
                    </fieldset>
                  );
                })}
              </>
            );
          }}
        />
      )}

      <Button className="w-full" disabled={!isAnswered} onClick={onContinue} size="lg">
        Continue
      </Button>
    </div>
  );
};
